import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { ADMIN_SET_AFFILIATE_RATES, ADMIN_SET_CPM_LEADERBOARD_EXCLUSION } from "@/queries/queries";
import { ADMIN_SET_AI_ASSISTANT_ACCESS } from "@/queries/aiAssistant";
import { ADMIN_SET_FACEBOOK_CONNECTIONS_ACCESS } from "@/queries/socialConnections";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Search, Users, ChevronLeft, ChevronRight, Pencil, Eye, EyeOff, UserCheck, ShieldCheck, UserX, Film, Video, Download, UserCog, MessageCircle, Filter, BookOpen, Clock, Scissors, TrendingUp, Sparkles, Handshake } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import type { ChartConfig } from "@/components/ui/chart";
import {
  useAdminUsersQuery,
  useAdminStatsQuery,
  useAdminFunnelStatsQuery,
  useAdminUpdateUserSubscriptionMutation,
  useAdminImpersonateUserMutation,
  useAdminPlansQuery,
  AdminUsersQuery,
} from "@/generated/graphql";

// The list view selects a narrower subset of fields than the full AdminUser type,
// so handlers must accept exactly what the query returns.
type AdminUser = NonNullable<NonNullable<AdminUsersQuery['adminUsers']>['data']>[number];

type LeaderboardAdminUser = AdminUser & {
  cpm_leaderboard_excluded?: boolean | null;
  cpm_leaderboard_excluded_reason?: string | null;
};

function isLeaderboardExcluded(user: AdminUser): boolean {
  return !!(user as LeaderboardAdminUser).cpm_leaderboard_excluded;
}

function leaderboardExcludedReason(user: AdminUser): string {
  return (user as LeaderboardAdminUser).cpm_leaderboard_excluded_reason || "";
}
import { formatDateTime, formatDateInput, ddmmyyyyToISODate, isoDateToDDMMYYYY } from "@/lib/format";
import { formatHoursMinutes } from "@/lib/video-metadata";
import { isPartnerActive } from "@/lib/billing/isPartnerActive";
import { resolvePartnerPlanId } from "@/lib/billing/resolvePartnerPlanId";
import { AFFILIATE_DEFAULT_RATES, parseAffiliateRateField } from "@/lib/affiliates/parseAffiliateRateField";
import { useToast } from "@/hooks/use-toast";
import {
  UserReliabilityAccounts,
  UserReliabilitySummary,
} from "@/components/admin/UserReliabilityAccounts";

const COUNTRY_CODES: [string, string, string][] = [
  ["+55", "BR", "🇧🇷"],
  ["+1", "US", "🇺🇸"],
  ["+44", "GB", "🇬🇧"],
  ["+351", "PT", "🇵🇹"],
  ["+34", "ES", "🇪🇸"],
  ["+33", "FR", "🇫🇷"],
  ["+49", "DE", "🇩🇪"],
  ["+39", "IT", "🇮🇹"],
  ["+81", "JP", "🇯🇵"],
  ["+86", "CN", "🇨🇳"],
  ["+91", "IN", "🇮🇳"],
  ["+52", "MX", "🇲🇽"],
  ["+54", "AR", "🇦🇷"],
  ["+56", "CL", "🇨🇱"],
  ["+57", "CO", "🇨🇴"],
  ["+58", "VE", "🇻🇪"],
  ["+51", "PE", "🇵🇪"],
  ["+598", "UY", "🇺🇾"],
  ["+595", "PY", "🇵🇾"],
  ["+591", "BO", "🇧🇴"],
  ["+593", "EC", "🇪🇨"],
  ["+61", "AU", "🇦🇺"],
  ["+82", "KR", "🇰🇷"],
  ["+353", "IE", "🇮🇪"],
  ["+31", "NL", "🇳🇱"],
  ["+41", "CH", "🇨🇭"],
  ["+46", "SE", "🇸🇪"],
  ["+47", "NO", "🇳🇴"],
  ["+45", "DK", "🇩🇰"],
  ["+48", "PL", "🇵🇱"],
  ["+7", "RU", "🇷🇺"],
  ["+90", "TR", "🇹🇷"],
  ["+966", "SA", "🇸🇦"],
  ["+971", "AE", "🇦🇪"],
  ["+972", "IL", "🇮🇱"],
  ["+27", "ZA", "🇿🇦"],
  ["+234", "NG", "🇳🇬"],
  ["+20", "EG", "🇪🇬"],
  ["+62", "ID", "🇮🇩"],
  ["+63", "PH", "🇵🇭"],
  ["+66", "TH", "🇹🇭"],
  ["+84", "VN", "🇻🇳"],
  ["+60", "MY", "🇲🇾"],
  ["+65", "SG", "🇸🇬"],
];

// Sorted by prefix length desc so longer prefixes match first (e.g. +598 before +5)
const SORTED_CODES = [...COUNTRY_CODES].sort((a, b) => b[0].length - a[0].length);

function getCountryFromPhone(phone: string | null | undefined): { code: string; flag: string } | null {
  if (!phone) return null;
  const cleaned = phone.replace(/[\s\-()]/g, "");
  for (const [prefix, code, flag] of SORTED_CODES) {
    if (cleaned.startsWith(prefix)) return { code, flag };
  }
  return null;
}

function getWhatsAppLink(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/[^\d]/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits}`;
}

const AdminUsers = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState<string>("");
  const [partnersOnly, setPartnersOnly] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Edit user dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [editPlanId, setEditPlanId] = useState<string>("");
  const [editProcessingSeconds, setEditProcessingSeconds] = useState<string>("");
  const [editClips, setEditClips] = useState<string>("");
  const [editLibraryVideoSeconds, setEditLibraryVideoSeconds] = useState<string>("");
  const [editLibraryExports, setEditLibraryExports] = useState<string>("");
  const [editLibraryCurations, setEditLibraryCurations] = useState<string>("");
  const [editObjective, setEditObjective] = useState<string>("");
  const [editIsPartner, setEditIsPartner] = useState(false);
  const [editPartnerExpiresAt, setEditPartnerExpiresAt] = useState<string>(""); // YYYY-MM-DD or ""
  const [editPartnerNote, setEditPartnerNote] = useState<string>("");
  const [editRatePayment, setEditRatePayment] = useState("");
  const [editRateCourse, setEditRateCourse] = useState("");
  const [editRateCpm, setEditRateCpm] = useState("");
  const [editRateNote, setEditRateNote] = useState("");
  const [savingRates, setSavingRates] = useState(false);
  const [editLeaderboardExcluded, setEditLeaderboardExcluded] = useState(false);
  const [editLeaderboardReason, setEditLeaderboardReason] = useState("");
  const [setAffiliateRates] = useMutation(ADMIN_SET_AFFILIATE_RATES);
  const [setLeaderboardExclusion] = useMutation(ADMIN_SET_CPM_LEADERBOARD_EXCLUSION);
  const [editAiAssistant, setEditAiAssistant] = useState(false);
  const [setAiAssistantAccess] = useMutation(ADMIN_SET_AI_ASSISTANT_ACCESS);
  const [editFacebookConnections, setEditFacebookConnections] = useState(false);
  const [setFacebookConnectionsAccess] = useMutation(ADMIN_SET_FACEBOOK_CONNECTIONS_ACCESS);

  const { data, loading, error, refetch } = useAdminUsersQuery({
    variables: {
      page,
      pageSize,
      search: search || undefined,
      plan_slug: planFilter || undefined,
      is_partner: partnersOnly ? true : undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  const { data: statsData } = useAdminStatsQuery({
    fetchPolicy: "cache-and-network",
  });

  const { data: funnelData } = useAdminFunnelStatsQuery({
    fetchPolicy: "cache-and-network",
  });

  const { data: plansData } = useAdminPlansQuery();

  const [updateUserSubscription, { loading: updating }] = useAdminUpdateUserSubscriptionMutation();
  const [impersonateUser] = useAdminImpersonateUserMutation();

  const users = data?.adminUsers?.data || [];
  const totalPages = data?.adminUsers?.pageCount || 1;
  const total = data?.adminUsers?.total || 0;

  const planCustomerCounts = statsData?.adminStats?.planCustomerCounts || [];
  const userStats = statsData?.adminStats?.users;
  const projectStats = statsData?.adminStats?.projects;
  const clipStats = statsData?.adminStats?.clips;
  const downloadStats = statsData?.adminStats?.youtubeDownloads;
  const plans = plansData?.adminPlans || [];
  const userTimeSeries = userStats?.timeSeries || [];
  const utmTimeSeries = userStats?.utmTimeSeries || [];

  // Extract unique UTM sources and assign colors
  const UTM_COLORS = [
    'hsl(220, 70%, 55%)', 'hsl(340, 70%, 55%)', 'hsl(160, 70%, 40%)',
    'hsl(30, 80%, 55%)', 'hsl(270, 60%, 55%)', 'hsl(190, 70%, 45%)',
    'hsl(0, 70%, 55%)', 'hsl(50, 80%, 45%)', 'hsl(300, 50%, 50%)',
    'hsl(130, 50%, 45%)',
  ];
  const utmSources = useMemo(() => {
    const sources = new Set<string>();
    utmTimeSeries.forEach((p) => sources.add(p.utm_source));
    return Array.from(sources).sort();
  }, [utmTimeSeries]);
  const [enabledUtmSources, setEnabledUtmSources] = useState<Set<string>>(new Set());
  const toggleUtmSource = (source: string) => {
    setEnabledUtmSources((prev) => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });
  };

  // Merge UTM data into chart data
  const chartData = useMemo(() => {
    if (enabledUtmSources.size === 0) return userTimeSeries;
    return userTimeSeries.map((point) => {
      const extra: Record<string, number> = {};
      for (const src of enabledUtmSources) {
        const match = utmTimeSeries.find((u) => u.date === point.date && u.utm_source === src);
        extra[`utm_${src}`] = match?.count ?? 0;
      }
      return { ...point, ...extra };
    });
  }, [userTimeSeries, utmTimeSeries, enabledUtmSources]);

  const chartConfig = useMemo(() => {
    const cfg: ChartConfig = {
      total: { label: t("admin.common.chartTotal"), color: "hsl(var(--primary))" },
    };
    for (const src of enabledUtmSources) {
      const idx = utmSources.indexOf(src);
      cfg[`utm_${src}`] = { label: src, color: UTM_COLORS[idx % UTM_COLORS.length] };
    }
    return cfg;
  }, [enabledUtmSources, utmSources, t]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const handleEditUser = (user: AdminUser) => {
    setSelectedUser(user);
    // If user has a plan, use it; otherwise find the Free plan (value === 0)
    const userPlanId = user.subscription?.plan?.id;
    const freePlan = plans.find((p) => p.value === 0);
    setEditPlanId(userPlanId || freePlan?.id || "");
    setEditProcessingSeconds(
      user.subscription?.current_month_processing_seconds?.toString() || "0"
    );
    setEditClips(
      user.subscription?.current_month_clips?.toString() || "0"
    );
    setEditLibraryVideoSeconds(
      user.subscription?.current_month_library_video_seconds?.toString() || "0"
    );
    setEditLibraryExports(
      user.subscription?.current_month_library_exports?.toString() || "0"
    );
    setEditLibraryCurations(
      user.subscription?.current_month_library_curations?.toString() || "0"
    );
    setEditObjective(user.onboarding_objective || "");
    setEditIsPartner(user.subscription?.is_partner ?? false);
    setEditPartnerExpiresAt(isoDateToDDMMYYYY(user.subscription?.partner_expires_at));
    setEditPartnerNote(user.subscription?.partner_note || "");
    setEditRatePayment(user.affiliate_rate_payment?.toString() ?? "");
    setEditRateCourse(user.affiliate_rate_course?.toString() ?? "");
    setEditRateCpm(user.affiliate_rate_cpm?.toString() ?? "");
    setEditRateNote(user.affiliate_rate_note ?? "");
    setEditLeaderboardExcluded(isLeaderboardExcluded(user));
    setEditLeaderboardReason(leaderboardExcludedReason(user));
    setEditAiAssistant(user.subscription?.ai_assistant_granted === true);
    setEditFacebookConnections(user.subscription?.facebook_connections_granted === true);
    setEditDialogOpen(true);
  };

  // Single place that turns the three raw rate inputs into validated,
  // send-ready values (or `undefined` when any field is invalid) — shared by
  // the dedicated "Salvar taxas" button and the dialog's primary Save so
  // they can never diverge on the blank-vs-zero invariant.
  type ParsedAffiliateRates = { payment: number; course: number; cpm: number; note: string | null };

  const parseEditedAffiliateRates = (): ParsedAffiliateRates | undefined => {
    const payment = parseAffiliateRateField(editRatePayment);
    const course = parseAffiliateRateField(editRateCourse);
    const cpm = parseAffiliateRateField(editRateCpm);
    if (payment === undefined || course === undefined || cpm === undefined) return undefined;
    return { payment, course, cpm, note: editRateNote || null };
  };

  const affiliateRatesDiffer = (parsed: ParsedAffiliateRates, user: AdminUser): boolean =>
    parsed.payment !== (user.affiliate_rate_payment ?? null) ||
    parsed.course !== (user.affiliate_rate_course ?? null) ||
    parsed.cpm !== (user.affiliate_rate_cpm ?? null) ||
    parsed.note !== (user.affiliate_rate_note ?? null);

  const sendAffiliateRates = (userId: string, parsed: ParsedAffiliateRates) =>
    setAffiliateRates({
      variables: {
        userId,
        input: {
          affiliate_rate_payment: parsed.payment,
          affiliate_rate_course: parsed.course,
          affiliate_rate_cpm: parsed.cpm,
          affiliate_rate_note: parsed.note,
        },
      },
    });

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    // Resolve the optional partner expiry (DD/MM/YYYY -> UTC end-of-day ISO).
    let partnerExpiresAtIso: string | null = null;
    if (editIsPartner && editPartnerExpiresAt) {
      const isoDate = ddmmyyyyToISODate(editPartnerExpiresAt);
      if (!isoDate) {
        toast({
          title: t("admin.users.partnerExpiresInvalid"),
          variant: "destructive",
        });
        return;
      }
      partnerExpiresAtIso = new Date(`${isoDate}T23:59:59Z`).toISOString();
    }

    // Validate the affiliate-rate fields up front: an invalid rate must
    // refuse the whole save (not silently drop the rate edit, not silently
    // send nothing while still closing the dialog with a success toast).
    const parsedRates = parseEditedAffiliateRates();
    if (!parsedRates) {
      toast({ title: t("admin.users.affiliateRates.invalid"), variant: "destructive" });
      return;
    }
    const ratesNeedSave = affiliateRatesDiffer(parsedRates, selectedUser);

    try {
      await updateUserSubscription({
        variables: {
          userId: selectedUser.id,
          input: {
            plan_id: resolvePartnerPlanId({ isPartner: editIsPartner, planId: editPlanId }),
            current_month_processing_seconds: editProcessingSeconds
              ? parseFloat(editProcessingSeconds)
              : undefined,
            current_month_clips: editClips
              ? parseInt(editClips, 10)
              : undefined,
            current_month_library_video_seconds: editLibraryVideoSeconds
              ? parseFloat(editLibraryVideoSeconds)
              : undefined,
            current_month_library_exports: editLibraryExports
              ? parseInt(editLibraryExports, 10)
              : undefined,
            current_month_library_curations: editLibraryCurations
              ? parseInt(editLibraryCurations, 10)
              : undefined,
            onboarding_objective: editObjective || undefined,
            is_partner: editIsPartner,
            partner_expires_at: editIsPartner ? partnerExpiresAtIso : null,
            partner_note: editIsPartner ? (editPartnerNote || null) : null,
          },
        },
      });

      if (ratesNeedSave) {
        await sendAffiliateRates(selectedUser.id, parsedRates);
      }

      const wasExcluded = isLeaderboardExcluded(selectedUser);
      const reasonChanged = editLeaderboardReason !== leaderboardExcludedReason(selectedUser);
      if (editLeaderboardExcluded !== wasExcluded || (editLeaderboardExcluded && reasonChanged)) {
        await setLeaderboardExclusion({
          variables: {
            userId: selectedUser.id,
            excluded: editLeaderboardExcluded,
            reason: editLeaderboardExcluded ? (editLeaderboardReason || null) : null,
          },
        });
      }

      if (editAiAssistant !== (selectedUser.subscription?.ai_assistant_granted === true)) {
        await setAiAssistantAccess({
          variables: { userId: selectedUser.id, enabled: editAiAssistant },
        });
      }

      if (editFacebookConnections !== (selectedUser.subscription?.facebook_connections_granted === true)) {
        await setFacebookConnectionsAccess({
          variables: { userId: selectedUser.id, enabled: editFacebookConnections },
        });
      }

      toast({
        title: t("admin.users.toast.updated"),
        description: t("admin.users.toast.updatedDesc"),
      });

      setEditDialogOpen(false);
      refetch();
    } catch {
      toast({
        title: t("admin.users.toast.updateError"),
        description: t("admin.users.toast.updateErrorDesc"),
        variant: "destructive",
      });
    }
  };

  const handleSaveAffiliateRates = async () => {
    if (!selectedUser) return;
    const parsed = parseEditedAffiliateRates();
    if (!parsed) {
      toast({ title: t("admin.users.affiliateRates.invalid"), variant: "destructive" });
      return;
    }
    setSavingRates(true);
    try {
      await sendAffiliateRates(selectedUser.id, parsed);
      toast({ title: t("admin.users.affiliateRates.saved") });
      refetch();
    } catch {
      toast({ title: t("admin.users.affiliateRates.error"), variant: "destructive" });
    } finally {
      setSavingRates(false);
    }
  };

  const handleImpersonateUser = async (user: AdminUser) => {
    try {
      const { data } = await impersonateUser({
        variables: { userId: user.id },
      });

      if (data?.adminImpersonateUser?.jwt) {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          localStorage.setItem('adminToken', currentToken);
        }
        localStorage.setItem('token', data.adminImpersonateUser.jwt);
        window.location.href = '/projects';
      }
    } catch {
      toast({
        title: t("admin.users.toast.impersonateError"),
        description: t("admin.users.toast.impersonateErrorDesc"),
        variant: "destructive",
      });
    }
  };

  const formatProcessingTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // A typed-but-invalid partner expiry (e.g. an incomplete or impossible date)
  // blocks saving; an empty field means an indefinite grant.
  const partnerExpiryInvalid =
    editIsPartner && editPartnerExpiresAt !== "" && ddmmyyyyToISODate(editPartnerExpiresAt) === null;

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">{t('admin.users.title')}</h1>
                <p className="text-muted-foreground">{t('admin.users.subtitle')}</p>
              </div>
            </div>
            <Badge variant="outline" className="text-red-500 border-red-500">
              ADMIN
            </Badge>
          </div>

          {/* Platform Overview Stats */}
          {userStats && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('admin.stats.platformOverview')}</h2>
              <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {t('admin.stats.totalUsers')}
                    </CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{userStats.total}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {t('admin.stats.confirmedUsers')}
                    </CardTitle>
                    <UserCheck className="h-4 w-4 text-green-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{userStats.confirmed}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {t('admin.stats.adminUsers')}
                    </CardTitle>
                    <ShieldCheck className="h-4 w-4 text-blue-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{userStats.admins}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {t('admin.stats.blockedUsers')}
                    </CardTitle>
                    <UserX className="h-4 w-4 text-red-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{userStats.blocked}</div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Users by Plan */}
          {planCustomerCounts.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('admin.stats.usersByPlan')}</h2>
              <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
                {planCustomerCounts.map((planStat) => (
                  <Card key={planStat.plan_id}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        {planStat.plan_name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{planStat.customer_count}</div>
                      <p className="text-xs text-muted-foreground">
                        {t('admin.users.usersOnPlan')}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Entity Stats (Projects, Clips, Downloads) */}
          {(projectStats || clipStats || downloadStats) && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('admin.stats.entityStats')}</h2>
              <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
                {/* Projects Stats */}
                {projectStats && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium">
                        {t('admin.stats.projects')}
                      </CardTitle>
                      <Film className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('admin.stats.total')}</span>
                        <span className="font-bold">{projectStats.total}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-600">{t('admin.stats.success')}</span>
                        <span className="font-medium">{projectStats.success}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-600">{t('admin.stats.processing')}</span>
                        <span className="font-medium">{projectStats.processing}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-600">{t('admin.stats.queued')}</span>
                        <span className="font-medium">{projectStats.queued}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-red-600">{t('admin.stats.failure')}</span>
                        <span className="font-medium">{projectStats.failure}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Clips Stats */}
                {clipStats && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium">
                        {t('admin.stats.clips')}
                      </CardTitle>
                      <Video className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('admin.stats.total')}</span>
                        <span className="font-bold">{clipStats.total}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-600">{t('admin.stats.success')}</span>
                        <span className="font-medium">{clipStats.success}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-600">{t('admin.stats.processing')}</span>
                        <span className="font-medium">{clipStats.processing}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-600">{t('admin.stats.queued')}</span>
                        <span className="font-medium">{clipStats.queued}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-red-600">{t('admin.stats.failure')}</span>
                        <span className="font-medium">{clipStats.failure}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Downloads Stats */}
                {downloadStats && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium">
                        {t('admin.stats.downloads')}
                      </CardTitle>
                      <Download className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('admin.stats.total')}</span>
                        <span className="font-bold">{downloadStats.total}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-600">{t('admin.stats.success')}</span>
                        <span className="font-medium">{downloadStats.success}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-600">{t('admin.stats.processing')}</span>
                        <span className="font-medium">{downloadStats.processing}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-600">{t('admin.stats.queued')}</span>
                        <span className="font-medium">{downloadStats.queued}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-red-600">{t('admin.stats.failure')}</span>
                        <span className="font-medium">{downloadStats.failure}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Daily Registrations Chart */}
          {userTimeSeries.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.users.dailyRegistrations')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ChartContainer config={chartConfig} className="h-[300px] w-full">
                  <BarChart data={chartData}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(v) => new Date(v + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis tickLine={false} axisLine={false} allowDecimals={false} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="total" fill="var(--color-total)" radius={[4, 4, 0, 0]} />
                    {Array.from(enabledUtmSources).map((src) => {
                      const idx = utmSources.indexOf(src);
                      return (
                        <Bar
                          key={src}
                          dataKey={`utm_${src}`}
                          fill={UTM_COLORS[idx % UTM_COLORS.length]}
                          radius={[4, 4, 0, 0]}
                        />
                      );
                    })}
                  </BarChart>
                </ChartContainer>

                {/* UTM source toggles */}
                {utmSources.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">{t('admin.users.utmSources')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {utmSources.map((src, idx) => {
                        const active = enabledUtmSources.has(src);
                        const color = UTM_COLORS[idx % UTM_COLORS.length];
                        return (
                          <button
                            key={src}
                            onClick={() => toggleUtmSource(src)}
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border transition-all ${
                              active
                                ? 'text-white shadow-sm'
                                : 'text-muted-foreground border-border hover:text-foreground'
                            }`}
                            style={active ? { backgroundColor: color, borderColor: color } : undefined}
                          >
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                            {src}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* User Funnels */}
          {funnelData?.adminFunnelStats && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold">{t('admin.users.funnel.title')}</h2>
              </div>
              <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                {/* Own Brand Funnel */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{t('admin.users.funnel.ownBrand')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {funnelData.adminFunnelStats.own_brand.map((step, index) => {
                      const maxCount = funnelData.adminFunnelStats.own_brand[0]?.count || 1;
                      const prevCount = index > 0 ? funnelData.adminFunnelStats.own_brand[index - 1].count : null;
                      const percentage = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
                      const conversionRate = prevCount && prevCount > 0 ? (step.count / prevCount) * 100 : null;

                      return (
                        <div key={step.label} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium">{t(`admin.users.funnel.${step.label}`)}</span>
                            <div className="flex items-center gap-2">
                              {conversionRate !== null && (
                                <span className="text-xs text-muted-foreground">
                                  {conversionRate.toFixed(1)}%
                                </span>
                              )}
                              <span className="font-semibold tabular-nums">{step.count.toLocaleString()}</span>
                            </div>
                          </div>
                          <div className="h-6 w-full bg-muted rounded overflow-hidden">
                            <div
                              className="h-full bg-primary rounded transition-all duration-500 flex items-center justify-end pr-2"
                              style={{ width: `${Math.max(percentage, 2)}%` }}
                            >
                              {percentage > 15 && (
                                <span className="text-xs text-primary-foreground font-medium">
                                  {percentage.toFixed(0)}%
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                {/* Other Channels Funnel */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{t('admin.users.funnel.otherChannels')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {funnelData.adminFunnelStats.other_channels.map((step, index) => {
                      const maxCount = funnelData.adminFunnelStats.other_channels[0]?.count || 1;
                      const prevCount = index > 0 ? funnelData.adminFunnelStats.other_channels[index - 1].count : null;
                      const percentage = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
                      const conversionRate = prevCount && prevCount > 0 ? (step.count / prevCount) * 100 : null;

                      return (
                        <div key={step.label} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium">{t(`admin.users.funnel.${step.label}`)}</span>
                            <div className="flex items-center gap-2">
                              {conversionRate !== null && (
                                <span className="text-xs text-muted-foreground">
                                  {conversionRate.toFixed(1)}%
                                </span>
                              )}
                              <span className="font-semibold tabular-nums">{step.count.toLocaleString()}</span>
                            </div>
                          </div>
                          <div className="h-6 w-full bg-muted rounded overflow-hidden">
                            <div
                              className="h-full bg-primary rounded transition-all duration-500 flex items-center justify-end pr-2"
                              style={{ width: `${Math.max(percentage, 2)}%` }}
                            >
                              {percentage > 15 && (
                                <span className="text-xs text-primary-foreground font-medium">
                                  {percentage.toFixed(0)}%
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('admin.users.list')}</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {t('admin.common.total')}: {total}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-2 mb-4 flex-wrap">
                <Input
                  placeholder={t('admin.users.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="max-w-sm"
                />
                <Select
                  value={planFilter}
                  onValueChange={(value) => {
                    setPlanFilter(value === "all" ? "" : value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={t('admin.users.filterByPlan')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.users.allPlans')}</SelectItem>
                    {plans.map((plan) => (
                      <SelectItem key={plan.slug} value={plan.slug}>
                        {plan.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
                  <Switch
                    checked={partnersOnly}
                    onCheckedChange={(v) => { setPartnersOnly(v); setPage(1); }}
                  />
                  {t('admin.users.partnersOnly')}
                </label>
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
              </form>

              {loading && !data ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">
                  {t('admin.common.error')}
                </div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('admin.users.name')}</TableHead>
                          <TableHead>{t('admin.users.email')}</TableHead>
                          <TableHead>{t('admin.users.reliability.column')}</TableHead>
                          <TableHead>{t('admin.users.whatsapp')}</TableHead>
                          <TableHead>{t('admin.users.country')}</TableHead>
                          <TableHead>{t('admin.users.plan')}</TableHead>
                          <TableHead>{t('admin.users.userType')}</TableHead>
                          <TableHead className="min-w-[220px]">{t('admin.users.usage')}</TableHead>
                          <TableHead>{t('admin.users.utmSource')}</TableHead>
                          <TableHead>{t('admin.users.payments')}</TableHead>
                          <TableHead>{t('admin.users.ltv')}</TableHead>
                          <TableHead>{t('admin.users.createdAt')}</TableHead>
                          <TableHead>{t('admin.users.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {user.name || "-"}
                                {user.is_admin && (
                                  <Badge variant="outline" className="text-red-500 border-red-500 text-xs">
                                    ADMIN
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="max-w-[220px] truncate" title={user.email || undefined}>
                              {user.email}
                            </TableCell>
                            <TableCell>
                              <UserReliabilitySummary accounts={user.account_reliabilities} />
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const link = getWhatsAppLink(user.phone_number);
                                if (!link) return <span className="text-muted-foreground">-</span>;
                                return (
                                  <a
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-green-600 hover:text-green-700 hover:underline"
                                  >
                                    <MessageCircle className="w-4 h-4" />
                                    {user.phone_number}
                                  </a>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const country = getCountryFromPhone(user.phone_number);
                                if (!country) return <span className="text-muted-foreground">-</span>;
                                return (
                                  <span title={country.code}>
                                    {country.flag} {country.code}
                                  </span>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col items-start gap-1">
                                <Badge variant="secondary">
                                  {user.subscription?.plan?.name || "Free"}
                                </Badge>
                                {user.subscription?.is_partner && (() => {
                                  const active = isPartnerActive(
                                    {
                                      isPartner: true,
                                      expiresAt: user.subscription.partner_expires_at ?? null,
                                    },
                                    new Date(),
                                  );
                                  const label = !active
                                    ? t('admin.users.partnerExpired')
                                    : user.subscription.partner_expires_at
                                      ? t('admin.users.partnerUntil', {
                                          date: formatDateTime(user.subscription.partner_expires_at).split(' ')[0],
                                        })
                                      : t('admin.users.partnerIndefiniteBadge');
                                  return (
                                    <Badge
                                      variant="outline"
                                      className={`whitespace-nowrap text-[10px] font-normal ${active
                                        ? "border-emerald-500 text-emerald-600"
                                        : "border-muted-foreground text-muted-foreground"}`}
                                      title={user.subscription.partner_note || undefined}
                                    >
                                      {label}
                                    </Badge>
                                  );
                                })()}
                                {(typeof user.affiliate_rate_payment === 'number'
                                  || typeof user.affiliate_rate_course === 'number'
                                  || typeof user.affiliate_rate_cpm === 'number') && (
                                  <Badge
                                    variant="outline"
                                    className="whitespace-nowrap text-[10px] font-normal border-sky-500 text-sky-600"
                                    title={user.affiliate_rate_note || undefined}
                                  >
                                    {t('admin.users.customRatesBadge')}
                                  </Badge>
                                )}
                                {user.subscription?.ai_assistant_granted && (
                                  <Badge
                                    variant="outline"
                                    className="whitespace-nowrap text-[10px] font-normal border-violet-500 text-violet-600"
                                  >
                                    {t('admin.users.aiAssistantBadge')}
                                  </Badge>
                                )}
                                {user.subscription?.facebook_connections_granted && (
                                  <Badge
                                    variant="outline"
                                    className="whitespace-nowrap text-[10px] font-normal border-blue-500 text-blue-600"
                                  >
                                    {t('admin.users.facebookConnectionsBadge')}
                                  </Badge>
                                )}
                                {isLeaderboardExcluded(user) && (
                                  <Badge
                                    variant="outline"
                                    className="whitespace-nowrap text-[10px] font-normal border-amber-500 text-amber-600"
                                    title={leaderboardExcludedReason(user) || undefined}
                                  >
                                    {t('admin.users.leaderboardHiddenBadge')}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col rounded-lg border border-border p-0.5 bg-muted/40 w-fit">
                                <button
                                  onClick={async () => {
                                    if (user.onboarding_objective === 'own_brand') return;
                                    try {
                                      await updateUserSubscription({ variables: { userId: user.id, input: { onboarding_objective: 'own_brand' } } });
                                      refetch();
                                    } catch { /* ignore */ }
                                  }}
                                  className={`flex items-center gap-1 rounded-md px-2.5 py-0.5 text-[11px] font-medium transition-all ${
                                    user.onboarding_objective === 'own_brand'
                                      ? 'bg-primary text-primary-foreground shadow-sm'
                                      : 'text-muted-foreground hover:text-foreground'
                                  }`}
                                >
                                  <TrendingUp className="w-3 h-3" />
                                  {t('admin.users.ownBrand')}
                                </button>
                                <button
                                  onClick={async () => {
                                    if (user.onboarding_objective === 'other_channels') return;
                                    try {
                                      await updateUserSubscription({ variables: { userId: user.id, input: { onboarding_objective: 'other_channels' } } });
                                      refetch();
                                    } catch { /* ignore */ }
                                  }}
                                  className={`flex items-center gap-1 rounded-md px-2.5 py-0.5 text-[11px] font-medium transition-all ${
                                    user.onboarding_objective === 'other_channels'
                                      ? 'bg-primary text-primary-foreground shadow-sm'
                                      : 'text-muted-foreground hover:text-foreground'
                                  }`}
                                >
                                  <Users className="w-3 h-3" />
                                  {t('admin.users.otherChannels')}
                                </button>
                              </div>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const timeUsed = user.subscription?.current_month_processing_seconds ?? 0;
                                const timeLimit = user.subscription?.plan?.processing_seconds ?? 0;
                                const timePercent = timeLimit > 0 ? Math.min((timeUsed / timeLimit) * 100, 100) : 0;

                                const clipsUsed = user.subscription?.current_month_clips ?? 0;
                                const clipsLimit = user.subscription?.plan?.max_clips ?? 0;
                                const clipsPercent = clipsLimit > 0 && clipsLimit < 1000000 ? Math.min((clipsUsed / clipsLimit) * 100, 100) : 0;

                                const libTimeUsed = user.subscription?.current_month_library_video_seconds ?? 0;
                                const libTimeLimit = user.subscription?.plan?.max_library_video_seconds ?? 0;
                                const libTimePercent = libTimeLimit > 0 ? Math.min((libTimeUsed / libTimeLimit) * 100, 100) : 0;

                                const libClipsUsed = user.subscription?.current_month_library_exports ?? 0;
                                const libClipsLimit = user.subscription?.plan?.max_library_exports ?? 0;
                                const libClipsPercent = libClipsLimit > 0 && libClipsLimit < 1000000 ? Math.min((libClipsUsed / libClipsLimit) * 100, 100) : 0;

                                return (
                                  <div className="min-w-[220px] space-y-1.5">
                                    {/* Processing time bar */}
                                    <div>
                                      <div className="flex items-center justify-between text-[11px] mb-0.5">
                                        <span className="flex items-center gap-1 text-muted-foreground">
                                          <Clock className="w-3 h-3" />
                                          {t('sidebar.time')}
                                        </span>
                                        <span className="tabular-nums text-muted-foreground">
                                          {formatHoursMinutes(timeUsed)} / {timeLimit > 0 ? formatHoursMinutes(timeLimit) : '-'}
                                          {timeLimit > 0 && <span className="ml-1 font-medium">{Math.round(timePercent)}%</span>}
                                        </span>
                                      </div>
                                      <Progress value={timePercent} className="h-1" />
                                    </div>

                                    {/* Clips bar */}
                                    <div>
                                      <div className="flex items-center justify-between text-[11px] mb-0.5">
                                        <span className="flex items-center gap-1 text-muted-foreground">
                                          <Scissors className="w-3 h-3" />
                                          {t('sidebar.clips')}
                                        </span>
                                        <span className="tabular-nums text-muted-foreground">
                                          {clipsUsed} / {clipsLimit >= 1000000 ? '∞' : clipsLimit}
                                          {clipsLimit > 0 && clipsLimit < 1000000 && <span className="ml-1 font-medium">{Math.round(clipsPercent)}%</span>}
                                        </span>
                                      </div>
                                      <Progress value={clipsPercent} className="h-1" />
                                    </div>

                                    {/* Library video time bar */}
                                    <div>
                                      <div className="flex items-center justify-between text-[11px] mb-0.5">
                                        <span className="flex items-center gap-1 text-muted-foreground">
                                          <BookOpen className="w-3 h-3" />
                                          {t('admin.users.library')}
                                        </span>
                                        <span className="tabular-nums text-muted-foreground">
                                          {formatHoursMinutes(libTimeUsed)} / {libTimeLimit > 0 ? formatHoursMinutes(libTimeLimit) : '-'}
                                          {libTimeLimit > 0 && <span className="ml-1 font-medium">{Math.round(libTimePercent)}%</span>}
                                        </span>
                                      </div>
                                      <Progress value={libTimePercent} className="h-1" />
                                    </div>

                                    {/* Library clips bar */}
                                    <div>
                                      <div className="flex items-center justify-between text-[11px] mb-0.5">
                                        <span className="flex items-center gap-1 text-muted-foreground">
                                          <Film className="w-3 h-3" />
                                          {t('sidebar.library')} {t('sidebar.clips').toLowerCase()}
                                        </span>
                                        <span className="tabular-nums text-muted-foreground">
                                          {libClipsUsed} / {libClipsLimit >= 1000000 ? '∞' : libClipsLimit}
                                          {libClipsLimit > 0 && libClipsLimit < 1000000 && <span className="ml-1 font-medium">{Math.round(libClipsPercent)}%</span>}
                                        </span>
                                      </div>
                                      <Progress value={libClipsPercent} className="h-1" />
                                    </div>
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-xs truncate max-w-[100px] block cursor-default">
                                      {user.utm_source || <span className="text-muted-foreground">direct</span>}
                                    </span>
                                  </TooltipTrigger>
                                  {(user.utm_medium || user.utm_campaign || user.utm_term || user.utm_content) && (
                                    <TooltipContent side="bottom" className="text-xs space-y-0.5">
                                      {user.utm_medium && <p>medium: {user.utm_medium}</p>}
                                      {user.utm_campaign && <p>campaign: {user.utm_campaign}</p>}
                                      {user.utm_term && <p>term: {user.utm_term}</p>}
                                      {user.utm_content && <p>content: {user.utm_content}</p>}
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                            <TableCell>
                              {user.subscription?.payment_count ?? 0}
                            </TableCell>
                            <TableCell>
                              {user.subscription?.total_payment_value != null
                                ? formatCurrency(user.subscription.total_payment_value)
                                : formatCurrency(0)}
                            </TableCell>
                            <TableCell>{formatDateTime(user.createdAt)}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Link to={`/admin/users/${user.id}`}>
                                  <Button variant="ghost" size="sm">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </Link>
                                <Link to={`/admin/users/${user.id}/conversations`}>
                                  <Button variant="ghost" size="sm" title={t('admin.botConversations.entryButton')}>
                                    <MessageCircle className="w-4 h-4" />
                                  </Button>
                                </Link>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditUser(user)}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleImpersonateUser(user)}
                                      >
                                        <UserCog className="w-4 h-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{t('admin.users.impersonate')}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {users.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                              {t('admin.common.noResults')}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-muted-foreground">
                      {t('admin.common.page')} {page} {t('admin.common.of')} {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('admin.users.editUser')}</DialogTitle>
            <DialogDescription>
              {t('admin.users.editUserDesc', { name: selectedUser?.name || selectedUser?.email })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Usage Preview Display */}
            {selectedUser && (() => {
              const selectedPlan = plans.find((p) => p.id === editPlanId);

              const metrics = [
                {
                  icon: <Clock className="w-3 h-3" />,
                  label: t('sidebar.time'),
                  currentUsed: selectedUser.subscription?.current_month_processing_seconds ?? 0,
                  newUsed: editProcessingSeconds ? parseFloat(editProcessingSeconds) : 0,
                  limit: Number(selectedPlan?.processing_seconds ?? 0),
                  formatValue: formatHoursMinutes,
                },
                {
                  icon: <Scissors className="w-3 h-3" />,
                  label: t('sidebar.clips'),
                  currentUsed: selectedUser.subscription?.current_month_clips ?? 0,
                  newUsed: editClips ? parseInt(editClips, 10) : 0,
                  limit: selectedPlan?.max_clips ?? 0,
                  formatValue: (v: number) => String(v),
                  unlimited: (selectedPlan?.max_clips ?? 0) >= 1000000,
                },
                {
                  icon: <BookOpen className="w-3 h-3" />,
                  label: t('admin.users.library'),
                  currentUsed: selectedUser.subscription?.current_month_library_video_seconds ?? 0,
                  newUsed: editLibraryVideoSeconds ? parseFloat(editLibraryVideoSeconds) : 0,
                  limit: selectedPlan?.max_library_video_seconds ?? 0,
                  formatValue: formatHoursMinutes,
                },
                {
                  icon: <Film className="w-3 h-3" />,
                  label: `${t('sidebar.library')} ${t('sidebar.clips').toLowerCase()}`,
                  currentUsed: selectedUser.subscription?.current_month_library_exports ?? 0,
                  newUsed: editLibraryExports ? parseInt(editLibraryExports, 10) : 0,
                  limit: selectedPlan?.max_library_exports ?? 0,
                  formatValue: (v: number) => String(v),
                  unlimited: (selectedPlan?.max_library_exports ?? 0) >= 1000000,
                },
                {
                  icon: <Sparkles className="w-3 h-3" />,
                  label: t('sidebar.curations'),
                  currentUsed: selectedUser.subscription?.current_month_library_curations ?? 0,
                  newUsed: editLibraryCurations ? parseInt(editLibraryCurations, 10) : 0,
                  limit: selectedPlan?.max_library_curations ?? 0,
                  formatValue: (v: number) => String(v),
                  unlimited: (selectedPlan?.max_library_curations ?? 0) >= 1000000,
                },
              ];

              return (
                <div className="space-y-2">
                  <Label>{t('admin.users.usagePreview')}</Label>
                  <div className="p-3 bg-muted rounded-lg space-y-3">
                    {metrics.map((m) => {
                      const hasChanges = m.currentUsed !== m.newUsed;
                      const limitDisplay = m.unlimited ? '∞' : m.limit > 0 ? m.formatValue(m.limit) : '-';
                      const effectiveLimit = m.unlimited ? 0 : m.limit;
                      const currentPct = effectiveLimit > 0 ? Math.min((m.currentUsed / effectiveLimit) * 100, 100) : 0;
                      const newPct = effectiveLimit > 0 ? Math.min((m.newUsed / effectiveLimit) * 100, 100) : 0;

                      return (
                        <div key={m.label} className="space-y-1">
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              {m.icon}
                              {t('admin.users.current')}
                            </span>
                            <span>{m.formatValue(m.currentUsed)} / {limitDisplay}</span>
                          </div>
                          {hasChanges ? (
                            <>
                              <div className="flex justify-between text-xs">
                                <span className="flex items-center gap-1 text-primary font-medium">
                                  {m.icon}
                                  {t('admin.users.afterChanges')}
                                </span>
                                <span className="text-primary font-medium">
                                  {m.formatValue(m.newUsed)} / {limitDisplay}
                                </span>
                              </div>
                              <Progress value={newPct} className="h-1.5" />
                              {effectiveLimit > 0 && (
                                <p className="text-xs text-center text-primary">
                                  {Math.round(newPct)}% {t('admin.users.used')}
                                </p>
                              )}
                            </>
                          ) : (
                            <>
                              <Progress value={currentPct} className="h-1.5" />
                              {effectiveLimit > 0 && (
                                <p className="text-xs text-muted-foreground text-center">
                                  {Math.round(currentPct)}% {t('admin.users.used')}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {selectedUser && (
              <UserReliabilityAccounts accounts={selectedUser.account_reliabilities} />
            )}

            <div className="space-y-2">
              <Label>{t('admin.users.userType')}</Label>
              <div className="flex flex-col rounded-lg border border-border p-0.5 bg-muted/40 w-fit">
                <button
                  type="button"
                  onClick={() => setEditObjective('own_brand')}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                    editObjective === 'own_brand'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <TrendingUp className="w-3.5 h-3.5" />
                  {t('admin.users.ownBrand')}
                </button>
                <button
                  type="button"
                  onClick={() => setEditObjective('other_channels')}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                    editObjective === 'other_channels'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Users className="w-3.5 h-3.5" />
                  {t('admin.users.otherChannels')}
                </button>
              </div>
            </div>

            <div className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Handshake className="w-4 h-4 text-primary" />
                  <Label htmlFor="isPartner" className="cursor-pointer">
                    {t('admin.users.partnerToggle')}
                  </Label>
                </div>
                <Switch
                  id="isPartner"
                  checked={editIsPartner}
                  onCheckedChange={(checked) => {
                    setEditIsPartner(checked);
                    if (!checked) {
                      // The plan can only be changed while the grant is on;
                      // turning it off reverts the picker to the user's current plan.
                      const freePlan = plans.find((p) => p.value === 0);
                      setEditPlanId(selectedUser?.subscription?.plan?.id || freePlan?.id || "");
                    }
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('admin.users.partnerToggleHint')}
              </p>
              {editIsPartner && (
                <div className="space-y-3 pt-1">
                  <div className="space-y-1">
                    <Label htmlFor="partnerExpiresAt">{t('admin.users.partnerExpiresAt')}</Label>
                    <Input
                      id="partnerExpiresAt"
                      type="text"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="DD/MM/YYYY"
                      value={editPartnerExpiresAt}
                      onChange={(e) => setEditPartnerExpiresAt(formatDateInput(e.target.value))}
                    />
                    {partnerExpiryInvalid ? (
                      <p className="text-xs text-destructive">
                        {t('admin.users.partnerExpiresInvalid')}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {editPartnerExpiresAt
                          ? t('admin.users.partnerExpiresAtHint')
                          : t('admin.users.partnerIndefinite')}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="partnerNote">{t('admin.users.partnerNote')}</Label>
                    <Input
                      id="partnerNote"
                      value={editPartnerNote}
                      onChange={(e) => setEditPartnerNote(e.target.value)}
                      placeholder={t('admin.users.partnerNotePlaceholder')}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <EyeOff className="w-4 h-4 text-primary" />
                  <Label htmlFor="leaderboardExcluded" className="cursor-pointer">
                    {t('admin.users.leaderboardHide')}
                  </Label>
                </div>
                <Switch
                  id="leaderboardExcluded"
                  checked={editLeaderboardExcluded}
                  onCheckedChange={setEditLeaderboardExcluded}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('admin.users.leaderboardHideHint')}
              </p>
              {editLeaderboardExcluded && (
                <div className="space-y-1 pt-1">
                  <Label htmlFor="leaderboardReason">{t('admin.users.leaderboardHideReason')}</Label>
                  <Input
                    id="leaderboardReason"
                    value={editLeaderboardReason}
                    onChange={(e) => setEditLeaderboardReason(e.target.value)}
                    placeholder={t('admin.users.leaderboardHideReasonPlaceholder')}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <Label htmlFor="aiAssistantAccess" className="cursor-pointer">
                    {t('admin.users.aiAssistantToggle')}
                  </Label>
                </div>
                <Switch
                  id="aiAssistantAccess"
                  checked={editAiAssistant}
                  onCheckedChange={setEditAiAssistant}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('admin.users.aiAssistantToggleHint')}
              </p>
            </div>

            <div className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="facebookConnectionsAccess" className="cursor-pointer">
                  {t('admin.users.facebookConnectionsToggle')}
                </Label>
                <Switch
                  id="facebookConnectionsAccess"
                  checked={editFacebookConnections}
                  onCheckedChange={setEditFacebookConnections}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('admin.users.facebookConnectionsToggleHint')}
              </p>
            </div>

            <div className="space-y-3 rounded-md border p-3">
              <div className="font-medium text-sm">{t("admin.users.affiliateRates.title")}</div>
              <div className="grid grid-cols-3 gap-2">
                {(
                  [
                    { key: "payment", labelKey: "admin.users.affiliateRates.payment", defaultValue: AFFILIATE_DEFAULT_RATES.payment, value: editRatePayment, setValue: setEditRatePayment },
                    { key: "course", labelKey: "admin.users.affiliateRates.course", defaultValue: AFFILIATE_DEFAULT_RATES.course, value: editRateCourse, setValue: setEditRateCourse },
                    { key: "cpm", labelKey: "admin.users.affiliateRates.cpm", defaultValue: AFFILIATE_DEFAULT_RATES.cpm, value: editRateCpm, setValue: setEditRateCpm },
                  ] as const
                ).map(({ key, labelKey, defaultValue, value, setValue }) => {
                  const inputId = `affiliateRate-${key}`;
                  return (
                    <div key={key}>
                      <Label htmlFor={inputId} className="text-xs">{t(labelKey)}</Label>
                      <Input
                        id={inputId}
                        type="number" min={0} max={100}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder={t("admin.users.affiliateRates.placeholderDefault", { value: defaultValue })}
                      />
                    </div>
                  );
                })}
              </div>
              <div>
                <Label htmlFor="affiliateRateNote" className="text-xs">{t("admin.users.affiliateRates.note")}</Label>
                <Input
                  id="affiliateRateNote"
                  value={editRateNote}
                  onChange={(e) => setEditRateNote(e.target.value)}
                  placeholder={t("admin.users.affiliateRates.notePlaceholder")}
                />
              </div>
              <p className="text-xs text-muted-foreground">{t("admin.users.affiliateRates.hint")}</p>
              <p className="text-xs text-amber-600">{t("admin.users.affiliateRates.cpmWarning")}</p>
              <Button size="sm" variant="secondary" disabled={savingRates} onClick={handleSaveAffiliateRates}>
                {t("admin.users.affiliateRates.save")}
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="plan">{t('admin.users.plan')}</Label>
              <Select value={editPlanId} onValueChange={setEditPlanId} disabled={!editIsPartner}>
                <SelectTrigger>
                  <SelectValue placeholder={t('admin.users.selectPlan')} />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!editIsPartner && (
                <p className="text-xs text-muted-foreground">
                  {t('admin.users.planPartnerOnly')}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="processingSeconds">{t('admin.users.currentMonthSeconds')}</Label>
              <Input
                id="processingSeconds"
                type="number"
                step="0.01"
                value={editProcessingSeconds}
                onChange={(e) => setEditProcessingSeconds(e.target.value)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">
                {editProcessingSeconds && !isNaN(parseFloat(editProcessingSeconds))
                  ? formatProcessingTime(parseFloat(editProcessingSeconds))
                  : "0m 0s"}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="clips">{t('admin.users.currentMonthClips')}</Label>
              <Input
                id="clips"
                type="number"
                step="1"
                value={editClips}
                onChange={(e) => setEditClips(e.target.value)}
                placeholder="0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="libraryVideoSeconds">{t('admin.users.currentMonthLibrarySeconds')}</Label>
              <Input
                id="libraryVideoSeconds"
                type="number"
                step="0.01"
                value={editLibraryVideoSeconds}
                onChange={(e) => setEditLibraryVideoSeconds(e.target.value)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">
                {editLibraryVideoSeconds && !isNaN(parseFloat(editLibraryVideoSeconds))
                  ? formatProcessingTime(parseFloat(editLibraryVideoSeconds))
                  : "0m 0s"}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="libraryExports">{t('admin.users.currentMonthLibraryExports')}</Label>
              <Input
                id="libraryExports"
                type="number"
                step="1"
                value={editLibraryExports}
                onChange={(e) => setEditLibraryExports(e.target.value)}
                placeholder="0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="libraryCurations">{t('admin.users.currentMonthLibraryCurations')}</Label>
              <Input
                id="libraryCurations"
                type="number"
                step="1"
                value={editLibraryCurations}
                onChange={(e) => setEditLibraryCurations(e.target.value)}
                placeholder="0"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveUser} disabled={updating || partnerExpiryInvalid}>
              {updating ? t('admin.users.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminUsers;
